/**
 * client/src/app/aal-gate-panel.tsx
 *
 * In-shell AAL gate panel (#1588) — what renders in place of a route's
 * children when an authenticated user is below its `minAal` floor. The floor
 * itself is UNCHANGED (Friction Rule clause 3 — enforcement, not
 * presentation); only what fills the gap changes.
 *
 * This is a dedicated AAL gate. It owns the step-up action required to unlock
 * the current route and must remain available even if the Security Centre's
 * semantic posture read is unavailable. `ProtectionLevelSummary` is retained
 * here for that gate-specific action; it is not part of the Security Overview.
 *
 * The step-up runs IN PLACE: the panel invalidates the cached session on
 * success, the shell re-renders with the promoted AAL, and `Guarded` lets the
 * original route's children through — the user never leaves the page they
 * asked for.
 *
 * Honesty (Friction Rule clause 2, CLAUDE.md §AAL Thresholds): every claim on
 * this screen now comes from the panel, which states enrolment/possession facts
 * only — never that a live human is present, and never that a check ran when it
 * did not.
 */
import { Link } from "wouter";
import { AppShellLayout } from "@/components/layout/app-shell";
import { Card } from "@/components/ui/card";
import { ProtectionLevelSummary } from "@/pages/security-centre/components/ProtectionLevelSummary";
import { levelFreshPhrase, levelGateSentence } from "@shared/lib/security-posture/tier-ladder";
import type React from "react";

export interface AalGatePanelProps {
  /** The route's floor — only "AAL2"/"AAL3" are reachable here (an
   *  authenticated user's own AAL is never below "AAL1"). */
  minAal: "AAL2" | "AAL3";
  /** The user's current session AAL — display/shell context only, never an
   *  authorization input (Guarded already made the gate decision). */
  currentAal: "AAL1" | "AAL2" | "AAL3";
  displayName?: string;
}

// Task 8 (AC-9): the legacy AAL-keyed protection-tier gate copy is retired —
// AAL2/AAL3 map to Level 2/3 (never Level 4, which additionally needs an
// enrolled guardian AAL alone never carries — see tier-ladder/format.ts).
// Fix round 1, minor 7: grammatical heading ("This page needs Level 2,
// fresh"), not the mid-sentence-comma "Level 2, fresh needed" fragment.
const HEADING: Record<"AAL2" | "AAL3", string> = {
  AAL2: `This page needs ${levelFreshPhrase(2)}`,
  AAL3: `This page needs ${levelFreshPhrase(3)}`,
};

/**
 * The one sentence the panel below cannot supply, because it does not know
 * which route was asked for: why this screen appeared at all. Kept short and
 * amber (never red — CLAUDE.md colour contract; this is a recoverable step,
 * not a fault).
 */
const REASON: Record<"AAL2" | "AAL3", string> = {
  // States a fact, never a promise: for some accounts there IS no route
  // available today (a second passkey still inside its 24h maturity window,
  // ledger #71(b)). Promising that raising it "here" will work would be the
  // same over-claim this screen is being fixed for; the panel below says what
  // is actually possible, including when the answer is "wait".
  AAL2: `${levelGateSentence(2)}. Your current protection level — and what it would take to raise it — is below.`,
  AAL3: `${levelGateSentence(3)}. Your current protection level — and what it would take to raise it — is below.`,
};

export function AalGatePanel({ minAal, currentAal, displayName }: AalGatePanelProps): React.JSX.Element {
  return (
    <AppShellLayout displayName={displayName} aal={currentAal}>
      <div data-testid="aal-gate-panel" className="max-w-[640px] mx-auto py-10 space-y-4">
        <Card className="space-y-2">
          <h1 className="text-[length:var(--text-heading-size)] font-semibold text-text-primary m-0">
            {HEADING[minAal]}
          </h1>

          <p
            data-testid="aal-gate-reason"
            className="text-[length:var(--text-caption-size)] text-warning m-0"
          >
            {REASON[minAal]}
          </p>

        </Card>

        {/* The single security-status view — the same component
            /security-centre renders. Its own reads are three-valued, so an
            unresolved one shows fewer claims rather than nothing; the heading,
            reason and links stay unconditional regardless. */}
        <Card>
          <ProtectionLevelSummary aal={currentAal} />
        </Card>

        <Card>
          {/* AC-84/#2997 (Task 13): these are wayfinding links, not a second
              "next step" — the composed single-CTA contract
              (tests/unit/aal-gate-panel-single-cta.test.tsx) counts every
              interactive element in `aal-gate-links` (this region) UNION
              `protection-next-step` (nested inside ProtectionLevelSummary)
              minus `data-secondary-cta`, mirroring Task 10's
              ProtectionLevelSummary rule, so composing this panel around that
              summary never yields one primary CTA per layer. Marked, not
              scoped out, so an unmarked addition here is caught by the same
              structural default the summary already uses. */}
          <div data-testid="aal-gate-links" className="flex flex-wrap items-center gap-4">
            <Link
              href="/security-centre"
              data-testid="aal-gate-security-centre-link"
              data-secondary-cta="true"
              className="text-[length:var(--text-caption-size)] text-interactive hover:underline"
            >
              Open Security Centre
            </Link>
            <Link
              href="/setup"
              data-testid="aal-gate-full-setup-link"
              data-secondary-cta="true"
              className="text-[length:var(--text-caption-size)] text-text-tertiary hover:text-interactive transition-colors duration-[var(--duration-fast)]"
            >
              Go to full setup instead
            </Link>
          </div>
        </Card>
      </div>
    </AppShellLayout>
  );
}
